import { useContext, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import setSelectedBusinessTravellersAndGuests from 'actions/businessTraveller/setSelectedBusinessTravellerEmployeesAndGuests'
import { saveBusinessTravellerSelectedTravellerState } from 'businessTraveller/storage/businessTravellerStorage'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'

/**
 * Hook for the "disabled" multi-room traveller chip (traveller role).
 * Ensures:
 *  1. OfferPage rooms context is initialised with 1 adult (the traveller themselves).
 *  2. The current employee is auto-selected in Redux so the multi-room booking
 *     guard recognises that a traveller has been selected.
 */
export function useMultiRoomDisabledChipTravellerInit(
  businessAccount: App.BusinessTraveller.CurrentBusinessAccount | undefined,
) {
  // 1. Ensure rooms are set in OfferPage context (1 traveller = 1 room).
  const { rooms } = useContext(OfferPageStateContext)
  const offerPageDispatch = useOfferPageDispatch()
  useEffect(() => {
    if (!rooms) {
      offerPageDispatch({ type: OfferPageActions.setRooms, rooms: [generateRoomOccupants({ adults: 1 })] })
    }
  }, [rooms, offerPageDispatch])

  // 2. Auto-select the current employee in Redux.
  const reduxDispatch = useAppDispatch()
  const selectedTravellerEmployeeIds = useAppSelector(state => state.businessTraveller.selectedTravellerEmployeeIds)
  useEffect(() => {
    const employeeId = businessAccount?.employee?.id
    if (employeeId && selectedTravellerEmployeeIds?.length === 0) {
      reduxDispatch(setSelectedBusinessTravellersAndGuests([employeeId], 0))
      saveBusinessTravellerSelectedTravellerState([employeeId], 0)
    }
  }, [businessAccount?.employee?.id, selectedTravellerEmployeeIds, reduxDispatch])
}
